import React, { useState, useEffect, useCallback } from 'react';
import { Users } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { ScrollArea } from '../../../../components/ui/scroll-area';
import ApiService from '../../../../services/api.service';
import FiltersPanel from './components/FiltersPanel';
import UsersTable from './components/UsersTable';

function UsersPageV3() {
  const { department } = useParams();

  const [usersData, setUsersData] = useState([]);
  const [growLevels, setGrowLevels] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [emailFilter, setEmailFilter] = useState('');
  const [archived, setArchived] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [usersSort, setUsersSort] = useState('-level');
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const fetchUsers = useCallback(
    async (skip, limit, sort, email) => {
      try {
        setIsLoading(true);
        const response = await ApiService.head.getDepartmentMembers(department, {
          filter: {
            levels: selectedLevels,
            email: email || undefined,
            archived,
          },
          skip: skip * limit,
          limit,
          sort,
        });

        setCurrentPage(skip);
        setUsersData(response.members);
        setTotalCount(response.total);
        setIsLoading(false);
      } catch (err) {
        console.error(err);
        setIsLoading(false);
      }
    },
    [department, selectedLevels, archived],
  );

  useEffect(() => {
    if (isLoading) {
      fetchUsers(currentPage, rowsPerPage, usersSort, emailFilter);
    }
  }, [isLoading]);

  useEffect(() => {
    fetchUsers(currentPage, rowsPerPage, usersSort, emailFilter);
  }, [selectedLevels, archived]);

  useEffect(() => {
    fetchUsers(0, rowsPerPage, usersSort, emailFilter);
  }, [department]);

  useEffect(() => {
    ApiService.head.getGrowLevels(department).then((res) => {
      setGrowLevels(res.levels);
    });
  }, [department]);

  const debouncedSearch = useDebouncedCallback((value) => {
    setEmailFilter(value);
    fetchUsers(0, rowsPerPage, usersSort, value);
  }, 500);

  const handleSearchChange = (value) => {
    debouncedSearch(value);
  };

  const handleArchiveUser = (userId) => {
    ApiService.head
      .archiveUser(userId)
      .then(() => {
        setMessage('User archived successfully.');
        setIsLoading(true);
      })
      .catch(({ response }) => {
        setError(response?.data?.messages || 'Failed to archive user');
      });
  };

  const handleUnArchiveUser = (userId) => {
    ApiService.head
      .unArchiveUser(userId)
      .then(() => {
        setMessage('User unarchived successfully.');
        setIsLoading(true);
      })
      .catch(({ response }) => {
        setError(response?.data?.messages || 'Failed to unarchive user');
      });
  };

  return (
    <ScrollArea className="h-full">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Users className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Users</h1>
            <p className="text-muted-foreground text-sm">
              Manage team members in {department} department
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex gap-6">
          {/* Filters Sidebar */}
          <div className="w-64 flex-shrink-0">
            <FiltersPanel
              growLevels={growLevels}
              selectedLevels={selectedLevels}
              setSelectedLevels={setSelectedLevels}
              archived={archived}
              setArchived={setArchived}
              onSearchChange={handleSearchChange}
              isDisabled={isLoading}
              setIsDisabled={setIsLoading}
            />
          </div>

          {/* Users Table */}
          <div className="flex-1 min-w-0">
            <UsersTable
              usersData={usersData}
              setIsLoading={setIsLoading}
              isLoading={isLoading}
              onArchiveUser={handleArchiveUser}
              onUnArchiveUser={handleUnArchiveUser}
              growLevels={growLevels}
              setSort={setUsersSort}
              page={currentPage}
              setPage={setCurrentPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              totalCount={totalCount}
            />
          </div>
        </div>
      </div>

      {/* Success Message Toast */}
      {message && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-md shadow-lg flex items-center gap-2">
          {message}
          <button
            type="button"
            onClick={() => setMessage('')}
            className="font-bold hover:opacity-80"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Message Toast */}
      {error && (
        <div className="fixed bottom-4 right-4 bg-destructive text-destructive-foreground px-4 py-2 rounded-md shadow-lg flex items-center gap-2">
          {error}
          <button type="button" onClick={() => setError('')} className="font-bold hover:opacity-80">
            ×
          </button>
        </div>
      )}
    </ScrollArea>
  );
}

export default UsersPageV3;
